/**
 * @file 汎用ユーティリティ関数群
 *
 * 空文字判定、郵便番号整形、日付整形（各種フォーマット）、
 * 全角⇒半角変換など、アプリケーション全体で使用される
 * 基本的なデータ加工関数。
 *
 * @param {undefined | null | string | string[]}variable 検索する変数
 * @returns {boolean} true: 空文字
 */
export function isEmpty(variable: undefined | null | string | string[]): boolean {
  const result: boolean = !!(variable === undefined || variable === null || variable === '' || (Array.isArray(variable) === true && (variable as string[] | string[]).length < 1));
  return result;
}

/**
 * 郵便番号の整形
 * @export
 * @param {string}zipCode 整形元の郵便番号: 1230005
 * @returns {string} 整形済み郵便番号: 123-0005
 */
export function formatZipCode(zipCode: string): string {
  if (isEmpty(zipCode) === true || zipCode.length < 7) {
    return '';
  }
  const result: string = `${zipCode.slice(0, 3)}-${zipCode.slice(3)}`;
  return result;
}

/**
 * サービス開始前を表す日付かどうか
 * @export
 * @param {Date}target 検査する日付
 * @returns {boolean} true:サービス開始前
 */
export function isBeforeStartDate(target: Date): boolean {
  if (target.getFullYear() === 9999 && target.getMonth() + 1 === 12 && target.getDate() === 31) {
    return true;
  }
  return false;
}

/**
 * 日付の整形
 * @export
 * @param {string}origin 整形元の文字列: 2022-01-23
 * @returns {string} 整形済み日付: 2022年1月23日
 */
export function formatDate(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 10) {
    return origin;
  }
  const originDate: Date = new Date(origin);
  // 9999-12-31は「-」にする
  if (isBeforeStartDate(originDate)) {
    return '-';
  }
  const result: string = `${originDate.getFullYear()}年${originDate.getMonth() + 1}月${originDate.getDate()}日`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {string}origin 整形元の文字列: 20220123
 * @returns {string} 整形済み日付: 2022/01/23
 */
export function formatDateStringYYYYMMDD(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 8) {
    return origin;
  }
  const year = parseInt(origin.substring(0, 4), 10);
  const month = parseInt(origin.substring(4, 6), 10);
  const day = parseInt(origin.substring(6, 8), 10);
  const originDate: Date = new Date(year, month - 1, day);
  return formatDatePadding(originDate);
}

/**
 * 年月の整形
 * @export
 * @param {string}origin 整形元の文字列: 2022-01
 * @returns {string} 整形済み年月: 2022年1月
 */
export function formatYearMonth(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 7) {
    return origin;
  }
  const originDate: Date = new Date(origin);
  const result: string = `${originDate.getFullYear()}年${originDate.getMonth() + 1}月`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date}origin 日付
 * @returns {string} 整形済み日付: 2022/1/23
 */
export function formatDateDefault(origin: Date): string {
  const result: string = `${origin.getFullYear()}/${origin.getMonth() + 1}/${origin.getDate()}`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date}origin 日付
 * @returns {string} 整形済み日付: 2022/01/23
 */
export function formatDatePadding(origin: Date): string {
  const yyyy = origin.getFullYear();
  const mm = `0${origin.getMonth() + 1}`.slice(-2);
  const dd = `0${origin.getDate()}`.slice(-2);
  const result: string = `${yyyy}/${mm}/${dd}`;
  return result;
}

/**
 * 日付の整形
 * @export
 * @param {Date}origin 日付
 * @returns {string} 整形済み日付: 2022-01-23
 */
export function formatDateHyphen(origin: Date): string {
  const yyyy = origin.getFullYear();
  const mm = `0${origin.getMonth() + 1}`.slice(-2);
  const dd = `0${origin.getDate()}`.slice(-2);
  const result: string = `${yyyy}-${mm}-${dd}`;
  return result;
}

/**
 * 年月の整形
 * @export
 * @param {string}origin 整形元の文字列: 202201
 * @returns {string} 整形済み年月: 2022年1月
 */
export function formatYYYYMM(origin: string): string {
  if (isEmpty(origin) === true || origin.length < 6) {
    return origin;
  }
  const year = parseInt(origin.substring(0, 4), 10);
  const month = parseInt(origin.substring(4, 6), 10);
  const originDate: Date = new Date(year, month - 1);
  const result: string = `${originDate.getFullYear()}年${originDate.getMonth() + 1}月`;
  return result;
}

/**
 * 全角から半角に変換
 */
export const toHalfWidth = (value: string): string => {
  return value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0)).replace(/\u3000/g, ' '); // 全角スペースを半角スペースに変換
};
